import type { JSX, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Loader } from '../ui/Loader';

interface ProtectedRouteProps {
  /** The element to render when authenticated */
  element: ReactNode;
  /** URL to redirect to if not authenticated. Defaults to "/" */
  redirectTo?: string;
  /** Custom fallback for auth errors */
  errorFallback?: ReactNode;
}

/**
 * Wrapper component that requires authentication.
 * Use inside a Route's element prop.
 *
 * @example
 * <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
 */
export const ProtectedRoute = ({ element, redirectTo = '/', errorFallback }: ProtectedRouteProps): JSX.Element => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <Loader fullscreen />;
  }

  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  const defaultErrorFallback = (
    <div className='error-container-fullscreen'>
      <h2 className='error-title'>Authentication Error</h2>
      <p className='error-message'>We encountered an issue with authentication. Please try again later.</p>
    </div>
  );

  return <ErrorBoundary fallback={errorFallback ?? defaultErrorFallback}>{element}</ErrorBoundary>;
};
